import React, { useState } from 'react';
import {
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import UserInspectViewFieldsMenu from './UserInspectViewFieldsMenu';
import { useLazyQuery } from '@apollo/client';
import {
  FieldContext,
  IGetCohortInspectFieldsQuery,
  IGetCohortInspectFieldsQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { GET_COHORT_INSPECT_FIELDS } from '../../../../utils/apollo/cohorts';
import * as BuilderUI from '../../../Builder/BuilderUI';
import SingleSelectLabel from '../../../Dropdown/SingleSelect/SingleSelectLabel';
import styled from 'styled-components';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';
import { useFloatingLayerContext } from '../../../FloatingManager';

interface IProps {
  schemaId: string;
  selectedFieldKeys: string[];
  onSubmit: (newSelection: string[]) => void;
}

const Menu = styled.div`
  width: 240px;
  ${floatingComponentStyles};
`;

const UserInspectViewFieldsFilter = ({
  schemaId,
  selectedFieldKeys,
  onSubmit,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [loadFields, { data, called }] = useLazyQuery<
    IGetCohortInspectFieldsQuery,
    IGetCohortInspectFieldsQueryVariables
  >(GET_COHORT_INSPECT_FIELDS, {
    variables: {
      term: '',
      func: null,
      events: [],
      includeFields: [],
      context: FieldContext.Inspect,
      schemaId,
    },
  });

  const fields = data?.fields ?? [];

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);

      if (open && !called) {
        loadFields();
      }
    },
    middleware: [shift(), offset(2)],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });
  const click = useClick(context, {
    toggle: false,
    keyboardHandlers: false,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const headingId = useId();
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleSubmit = (newSelection: string[]) => {
    onSubmit(newSelection);
    setIsOpen(false);
  };

  const label =
    selectedFieldKeys.length > 0
      ? `${selectedFieldKeys.length} Selected`
      : 'All';

  const zIndex = useFloatingLayerContext();

  return (
    <BuilderUI.BuilderFieldContainer>
      <BuilderUI.BuilderFieldLabel>Fields</BuilderUI.BuilderFieldLabel>
      <SingleSelectLabel
        ref={refs.setReference}
        {...getReferenceProps()}
        isAlwaysActive={true}
      >
        {label}
      </SingleSelectLabel>
      {isOpen && (
        <Menu
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex,
          }}
          aria-labelledby={headingId}
          {...getFloatingProps()}
        >
          <UserInspectViewFieldsMenu
            fields={fields}
            selectedFieldKeys={selectedFieldKeys}
            onSubmit={handleSubmit}
          />
        </Menu>
      )}
    </BuilderUI.BuilderFieldContainer>
  );
};

export default UserInspectViewFieldsFilter;
